import { useTheme } from "../../styles/theme";
import { useApp } from "../../context/AppContext";
import { dispDate, addDays } from "../../utils/dates";
import { fmt } from "../../utils/format";
import SectionTitle from "../../components/SectionTitle";
import FlowRow from "../../components/FlowRow";

export default function DailyPL() {
  const S = useTheme();
  const c = S.colors;
  const {
    data,
    form,
    setForm,
    save,
    dailySummaries,
    viewedDaySummary,
    dailyViewDate,
    setDailyViewDate,
    debts,
    setOpeningBalance,
    TODAY,
  } = useApp();

  const d = viewedDaySummary;

  return (
    <div>
      <SectionTitle>Daily Profit &amp; Loss</SectionTitle>

      {/* Date selector */}
      <div style={S.dateNavRow}>
        <button
          style={S.dateNavBtn}
          onClick={() => setDailyViewDate(addDays(dailyViewDate, -1))}
        >
          ◀ Prev
        </button>
        <input
          style={{ ...S.input, width: 170, textAlign: "center" }}
          type="date"
          value={dailyViewDate}
          onChange={(e) => setDailyViewDate(e.target.value)}
        />
        <button
          style={S.dateNavBtn}
          onClick={() => setDailyViewDate(addDays(dailyViewDate, 1))}
        >
          Next ▶
        </button>
        <button
          style={{
            ...S.dateNavBtn,
            background: c.okBg,
            color: c.primary,
            border: `1px solid ${c.okBorder}`,
          }}
          onClick={() => setDailyViewDate(TODAY)}
        >
          Today
        </button>
      </div>

      {/* Single day detail */}
      <div>
        <div style={S.cashFlowPanel}>
          <div style={S.cashFlowLeft}>
            <div
              style={{
                fontWeight: 700,
                color: c.text,
                fontSize: 16,
                marginBottom: 16,
              }}
            >
              {dispDate(d.date)}
            </div>
            <FlowRow
              icon="🏦"
              label="Opening Balance"
              value={fmt(d.openingBalance)}
              color={c.info}
              sub={d.manualOB ? "manually set" : "from previous EOD"}
            />
            <div style={S.flowDivider} />
            <FlowRow
              icon="💰"
              label="Sales"
              value={`+ ${fmt(d.salesIn)}`}
              color={c.primary}
            />
            <div style={S.flowDivider} />
            <FlowRow
              icon="💸"
              label="Direct Expenses"
              value={`− ${fmt(d.expensesOut)}`}
              color={c.purple}
            />
            <FlowRow
              icon="🏪"
              label="Vendor Paid"
              value={`− ${fmt(d.vendorPaidOut)}`}
              color={c.warn}
            />
            <FlowRow
              icon="👤"
              label="Salaries"
              value={`− ${fmt(d.salariesOut)}`}
              color={c.indigo}
            />
            {d.debtPaidOut > 0 && (
              <FlowRow
                icon="🔴"
                label="Debt Repayments"
                value={`− ${fmt(d.debtPaidOut)}`}
                color={c.dangerLight}
              />
            )}
            <div style={S.flowDivider} />
            <FlowRow
              icon={d.netPL >= 0 ? "📈" : "📉"}
              label="Net P&L"
              value={(d.netPL >= 0 ? "+ " : "− ") + fmt(Math.abs(d.netPL))}
              color={d.netPL >= 0 ? c.primary : c.danger}
            />
          </div>
          <div style={S.cashFlowRight}>
            <div style={S.eodBox}>
              <div style={S.eodLabel}>💵 EOD Cash Balance</div>
              <div
                style={{
                  ...S.eodValue,
                  color: d.eodBalance >= 0 ? c.primary : c.danger,
                }}
              >
                {fmt(d.eodBalance)}
              </div>
              <div style={S.eodFormula}>
                {fmt(d.openingBalance)} + {fmt(d.salesIn)} −{" "}
                {fmt(
                  d.expensesOut + d.salariesOut + d.vendorPaidOut + d.debtPaidOut,
                )}
              </div>
            </div>
            {d.vendorDuePending > 0 && (
              <div style={S.eodPendingBox}>
                <div style={S.eodLabel}>⏳ Pending vendor (not paid yet)</div>
                <div
                  style={{ ...S.eodValue, fontSize: 18, color: c.warn }}
                >
                  {fmt(d.vendorDuePending)}
                </div>
                <div style={S.eodFormula}>
                  EOD after paying: {fmt(d.eodIfAllPaid)}
                </div>
              </div>
            )}
            <div style={S.eodStatusBox}>
              <div
                style={{
                  fontWeight: 700,
                  color:
                    d.netPL > 0 ? c.primary : d.netPL === 0 ? c.textDim : c.danger,
                  fontSize: 14,
                  textAlign: "center",
                }}
              >
                {d.netPL > 0
                  ? `✅ Profit of ${fmt(d.netPL)}`
                  : d.netPL === 0
                    ? "➡️ Break even"
                    : `❌ Loss of ${fmt(Math.abs(d.netPL))}`}
              </div>
            </div>
          </div>
        </div>

        {/* Transactions that day */}
        {(() => {
          const txs = [
            ...data.sales
              .filter((s) => s.date === d.date)
              .map((s) => ({
                type: "Income",
                label: `${s.client}${s.description ? ` — ${s.description}` : ""}`,
                amount: s.amount,
                sign: 1,
              })),
            ...(data.expenses || [])
              .filter((e) => e.date === d.date)
              .map((e) => ({
                type: "Expense",
                label: `${e.category}: ${e.description}`,
                amount: e.amount,
                sign: -1,
              })),
            ...data.purchases
              .filter((p) => p.payDue === d.date)
              .map((p) => ({
                type: "Vendor",
                label: `${p.vendor}: ${p.item || "Purchase"}${p.paid ? " ✓" : " ⏳"}`,
                amount: p.amount,
                sign: -1,
              })),
            ...data.salaries
              .filter((s) => s.date === d.date)
              .map((s) => ({
                type: "Salary",
                label: `${s.employee}${s.note ? ` — ${s.note}` : ""}`,
                amount: s.amount,
                sign: -1,
              })),
            ...debts.flatMap((d2) =>
              (d2.payments || [])
                .filter((p) => p.date === d.date)
                .map((p) => ({
                  type: "Debt",
                  label: `${d2.vendor} — ${p.fullClear ? "Full clear" : "Partial"}`,
                  amount: p.amount,
                  sign: -1,
                })),
            ),
          ];
          if (txs.length === 0) return null;
          return (
            <>
              <SectionTitle style={{ marginTop: 24 }}>
                Transactions on {dispDate(d.date)}
              </SectionTitle>
              <div style={S.txList}>
                {txs.map((tx, i) => {
                  const tColor =
                    {
                      Income: c.primary,
                      Expense: c.purple,
                      Vendor: c.warn,
                      Salary: c.indigo,
                      Debt: c.dangerLight,
                    }[tx.type] || c.textMuted;
                  return (
                    <div key={i} style={S.txRow}>
                      <span
                        style={{
                          ...S.typeBadge,
                          background: tColor + "22",
                          color: tColor,
                        }}
                      >
                        {tx.type}
                      </span>
                      <span style={S.txLabel}>{tx.label}</span>
                      <span
                        style={{
                          ...S.txAmt,
                          color: tx.sign > 0 ? c.primary : c.dangerLight,
                        }}
                      >
                        {tx.sign > 0 ? "+" : "−"} {fmt(tx.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>

      {/* Daily P&L History table */}
      <SectionTitle style={{ marginTop: 32 }}>Daily P&L History</SectionTitle>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {[
                "Date",
                "Opening Balance",
                "Sales IN",
                "Expenses OUT",
                "Vendor OUT",
                "Salary OUT",
                "Net P&L",
                "EOD Cash",
                "Status",
              ].map((h) => (
                <th key={h} style={S.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dailySummaries.length === 0 && (
              <tr>
                <td colSpan={9} style={S.emptyCell}>
                  No data yet. Start adding sales, expenses, and purchases.
                </td>
              </tr>
            )}
            {[...dailySummaries].reverse().map((ds) => (
              <tr
                key={ds.date}
                style={{
                  ...S.tr,
                  background: ds.date === TODAY ? c.okBg : undefined,
                  cursor: "pointer",
                }}
                onClick={() => setDailyViewDate(ds.date)}
              >
                <td
                  style={{
                    ...S.td,
                    fontWeight: ds.date === TODAY ? 700 : 400,
                    color: ds.date === TODAY ? c.primary : c.textBody,
                  }}
                >
                  {dispDate(ds.date)}
                  {ds.date === TODAY ? " ← Today" : ""}
                </td>
                <td style={S.td}>{fmt(ds.openingBalance)}</td>
                <td style={{ ...S.td, color: c.primary, fontWeight: 600 }}>
                  {fmt(ds.salesIn)}
                </td>
                <td style={{ ...S.td, color: c.purple }}>
                  {fmt(ds.expensesOut)}
                </td>
                <td style={{ ...S.td, color: c.warn }}>
                  {fmt(ds.vendorPaidOut)}
                  {ds.vendorDuePending > 0 && (
                    <span style={{ color: c.dangerLight, fontSize: 10 }}>
                      {" "}
                      +{fmt(ds.vendorDuePending)} due
                    </span>
                  )}
                </td>
                <td style={{ ...S.td, color: c.indigo }}>
                  {fmt(ds.salariesOut)}
                </td>
                <td
                  style={{
                    ...S.td,
                    fontWeight: 700,
                    color: ds.netPL >= 0 ? c.primary : c.danger,
                  }}
                >
                  {ds.netPL >= 0 ? "+" : ""}
                  {fmt(ds.netPL)}
                </td>
                <td
                  style={{
                    ...S.td,
                    fontWeight: 700,
                    color: ds.eodBalance >= 0 ? c.info : c.danger,
                  }}
                >
                  {fmt(ds.eodBalance)}
                </td>
                <td style={S.td}>
                  {ds.netPL > 0 ? (
                    <span style={S.badgePaid}>✅ Profit</span>
                  ) : ds.netPL < 0 ? (
                    <span style={S.badgeDue}>❌ Loss</span>
                  ) : (
                    <span style={S.badgePending}>➡️ Even</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Opening balance manager */}
      <SectionTitle style={{ marginTop: 32 }}>Set Opening Balance</SectionTitle>
      <p style={S.tabDesc}>
        Set the cash-in-hand at the start of any day. Each day's opening
        auto-flows from the previous day's EOD unless you override it here.
      </p>
      <div style={S.formCard}>
        <div style={S.formGrid}>
          <div style={S.formField}>
            <label style={S.label}>Date</label>
            <input
              style={S.input}
              type="date"
              value={form.obDate || TODAY}
              onChange={(e) => setForm({ ...form, obDate: e.target.value })}
            />
          </div>
          <div style={S.formField}>
            <label style={S.label}>Opening Cash (₹)</label>
            <input
              style={S.input}
              type="number"
              placeholder="e.g. 10000"
              value={form.obAmount || ""}
              onChange={(e) => setForm({ ...form, obAmount: e.target.value })}
            />
          </div>
          <div style={S.formField}>
            <label style={S.label}>Note (optional)</label>
            <input
              style={S.input}
              placeholder="e.g. Counted from safe"
              value={form.obNote || ""}
              onChange={(e) => setForm({ ...form, obNote: e.target.value })}
            />
          </div>
        </div>
        <div style={{ ...S.creditNote, borderLeftColor: c.info }}>
          🏦 If you set an opening balance for a date, it overrides the
          auto-calculated flow from the previous day.
        </div>
        <button
          style={{ ...S.submitBtn, background: c.info, color: c.onInfo }}
          onClick={setOpeningBalance}
        >
          Set Opening Balance
        </button>
      </div>

      {(data.openingBalances || []).length > 0 && (
        <>
          <SectionTitle style={{ marginTop: 24 }}>
            Opening Balance History
          </SectionTitle>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Date", "Amount", "Note", "Action"].map((h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...(data.openingBalances || [])].reverse().map((b, i) => (
                  <tr key={i} style={S.tr}>
                    <td style={S.td}>
                      {dispDate(b.date)}
                      {b.date === TODAY ? " (Today)" : ""}
                    </td>
                    <td
                      style={{ ...S.td, fontWeight: 700, color: c.info }}
                    >
                      {fmt(b.amount)}
                    </td>
                    <td style={S.td}>{b.note || "—"}</td>
                    <td style={S.td}>
                      <button
                        style={{ ...S.smallBtn, ...S.deleteBtn }}
                        onClick={() =>
                          save({
                            ...data,
                            openingBalances: (
                              data.openingBalances || []
                            ).filter((x) => x.date !== b.date),
                          })
                        }
                      >
                        ✕ Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
